import { createFileRoute, Link } from "@tanstack/react-router";
import identiteUrl from "@/assets/portrait-637w.webp";
import identite2xUrl from "@/assets/portrait-1274w.webp";
import { HomeHighlights, highlightsQueryOptions } from "@/components/HomeHighlights";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { title: "Mathys Gautier — Datajournaliste en devenir" },
      {
        name: "description",
        content:
          "Mathys Gautier, en master à Sciences Po Paris, se forme au datajournalisme, à l’investigation, à l’OSINT et à l’analyse de données.",
      },
      { name: "robots", content: "noimageindex" },
      { property: "og:title", content: "Mathys Gautier — Datajournaliste en devenir" },
      {
        property: "og:description",
        content:
          "Mathys Gautier, en master à Sciences Po Paris, se forme au datajournalisme, à l’investigation, à l’OSINT et à l’analyse de données.",
      },
      { property: "og:url", content: "https://mathysgautier.com/" },
      { property: "og:site_name", content: "Mathys Gautier, portfolio" },
    ],

    links: [
      { rel: "canonical", href: "https://mathysgautier.com/" },
      { rel: "preload", as: "image", href: identiteUrl, fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Mathys Gautier",
          jobTitle: "Datajournaliste",
          url: "https://mathysgautier.com/",
          image: `https://mathysgautier.com${identiteUrl}`,
          description:
            "Mathys Gautier, en master à Sciences Po Paris, se forme au datajournalisme, à l’investigation, à l’OSINT et à l’analyse de données.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Mathys Gautier — Datajournaliste en devenir",
          url: "https://mathysgautier.com/",
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(highlightsQueryOptions),
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-5xl px-6 py-24 text-sm">
      {error instanceof Error ? error.message : "Une erreur est survenue."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-5xl px-6 py-24 text-sm">Contenu introuvable.</div>
  ),
  component: Home,
});

function Home() {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-16 pb-8 sm:pt-24 sm:pb-24">
      <div className="grid gap-16 lg:grid-cols-[1.1fr_0.9fr] lg:grid-rows-[auto_auto] lg:gap-20">
        <div className="order-1 flex flex-col justify-center lg:order-none lg:col-start-1 lg:row-start-1 lg:row-end-2">
          <p className="eyebrow uppercase text-ocean mb-12">Datajournaliste en devenir</p>
          <h1 className="mt-12 font-display text-5xl leading-[1.02] tracking-tight sm:text-7xl">
            Fouiller et croiser <br />
            <span className="italic text-ocean">les données,</span>
            <br />
            pour décrypter le réel.
          </h1>
          <p className="mt-16 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Actuellement en master à l’École de Journalisme de Sciences Po Paris, doublement diplômé
            en sciences sociales (Sciences Po) et en mathématiques (Université Paris Cité), je me
            spécialise progressivement dans le datajournalisme en me formant à l’investigation,
            l’OSINT et l’analyse de données.
          </p>

          <div className="mt-10 flex flex-col items-stretch gap-4 text-sm sm:flex-row sm:flex-wrap sm:items-center">
            <Link
              to="/portfolio"
              className="micro-button group inline-flex w-full items-center justify-center rounded-full bg-ocean-700 px-6 py-3 font-semibold text-paper shadow-sm hover:bg-ocean-800 sm:w-auto"
              style={{ color: "var(--paper)" }}
            >
              Voir les articles parus
            </Link>
            <Link
              to="/parcours"
              className="micro-button group inline-flex w-full items-center justify-center rounded-full border border-foreground bg-transparent px-6 py-3 text-foreground hover:bg-foreground hover:text-paper sm:w-auto"
            >
              Mon parcours
            </Link>
            <Link
              to="/contact"
              className="group inline-flex w-full items-center justify-center px-6 py-3 text-foreground micro-interactive active:scale-95 sm:w-auto"
            >
              Prendre contact
            </Link>
          </div>

          <dl className="mt-16 grid grid-cols-3 gap-8 border-t border-border pt-8 pb-8 max-sm:pt-4 max-sm:pb-4">
            {[
              { k: "5", v: "expériences dans les médias", to: "/parcours" as const, hash: "journalisme" as const },
              { k: "18", v: "podcasts réalisés et en ligne", to: "/portfolio" as const, hash: "section-radio-germaine" as const },
              { k: "1000+", v: "lignes de code sur RStudio", to: "/dataviz" as const },
            ].map((s) => (
              <div key={s.v} className="group">
                <dt className="font-serif text-3xl text-ocean">
                  <Link
                    to={s.to}
                    hash={s.hash}
                    aria-label={`${s.k} ${s.v}`}
                    className="block micro-interactive active:scale-[0.98]"
                  >
                    {s.k}
                  </Link>
                </dt>
                <dd className="mt-2 text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors group-hover:text-foreground">
                  {s.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="order-3 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-end-2 lg:self-stretch">
          <div className="lg:sticky lg:top-24">
            <figure className="relative">
              <div
                className="absolute -inset-x-4 -inset-y-4 rounded-3xl border border-border"
                aria-hidden
              />
              <img
                src={identiteUrl}
                srcSet={`${identiteUrl} 637w, ${identite2xUrl} 1274w`}
                sizes="(max-width: 1023px) 100vw, 637px"
                alt="Portrait de Mathys Gautier"
                fetchPriority="high"
                decoding="async"
                width={637}
                height={956}
                className="relative aspect-[4/5] w-full rounded-2xl object-cover object-[50%_18%] grayscale"
              />
              <figcaption className="mt-4 flex justify-between text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <span>
                  Crédits : <span className="font-medium text-foreground">DR</span>
                </span>
                <span>Paris — 2025</span>
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="order-2 lg:order-none lg:col-span-2 lg:row-start-2">
          <HomeHighlights />
        </div>
      </div>
    </section>
  );
}
